export enum PricingMode {
  NO_PRICING = 'NO_PRICING',
  COST_ONLY = 'COST_ONLY',
  CUSTOMER_PRICING = 'CUSTOMER_PRICING'
}

// Where a pricing request comes from. The backend resolves every request to one
// of these and applies that module's switches (DEV-540).
export enum PricingModule {
  CATALOG = 'CATALOG',
  ORDERS = 'ORDERS',
  QUOTES = 'QUOTES'
}

export interface PricingModuleSettings {
  // Cache prices for this module in Redis (30 min TTL)?
  cache: boolean;
  // true  -> every price comes back isEditable = true
  // false -> keep whatever the pricing source returned (e.g. PJP contract lock)
  alwaysEditable: boolean;
}

// GET /pricing/configuration. Flat, unlike the old nested
// quoteConfiguration.pricing object it replaces.
export interface PricingConfigurationDTO {
  _id?: string;
  mode: PricingMode;
  defaultMarginEnabled: boolean;
  defaultMarginPercent: number;
  /**
   * DEV-795 — whether this tenant has cost data at all.
   *
   * `false` (Lindenmeyr / CNG) means the backend STRIPS every cost / GP /
   * margin field from its responses, exports and PDFs — the keys are absent,
   * not `0` or `null`. The UI must hide every cost and GP surface rather than
   * render the zeros those absent fields would otherwise compute to
   * (`$0.00`, `100%`).
   *
   * ABSENT on any tenant whose document predates the flag, and absent must read
   * as `true` — see `toPricingSettings`.
   */
  costAvailable?: boolean;
  // Backend-only concerns: `nonEditableSellTypes` feeds the strategies and
  // `modules[*].cache` is a server switch. Typed for completeness; the UI reads
  // neither.
  nonEditableSellTypes?: string[];
  modules?: Record<PricingModule, PricingModuleSettings>;
}

// The pricing subset the UI reads — this is what the config stores expose to
// components. Same shape the nested quoteConfiguration.pricing object had, so
// consumers did not have to change when the source moved.
export interface PricingSettings {
  mode: `${PricingMode}`;
  defaultMarginEnabled: boolean;
  defaultMarginPercent: number;
  /** See `PricingConfigurationDTO.costAvailable` (DEV-795). */
  costAvailable: boolean;
}

// Anything shaped enough to read pricing settings out of: the new document, the
// legacy nested object, or a partial of either.
export interface PricingSettingsSource {
  mode?: string;
  defaultMarginEnabled?: boolean;
  defaultMarginPercent?: number;
  costAvailable?: boolean;
}
